import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PrivacySettings = ({ settings, onChange }) => {
  const ipfsGatewayOptions = [
    { value: 'pinata', label: 'Pinata Gateway', description: 'Pinata IPFS gateway (Recommended)' },
    { value: 'ipfs-io', label: 'IPFS.io Gateway', description: 'Official IPFS gateway' },
    { value: 'cloudflare', label: 'Cloudflare Gateway', description: 'Cloudflare IPFS gateway' },
    { value: 'infura', label: 'Infura Gateway', description: 'Infura IPFS gateway' }
  ];

  const retentionOptions = [
    { value: '6-months', label: '6 Months', description: 'Data removed after 6 months' },
    { value: '1-year', label: '1 Year', description: 'Data removed after 1 year' },
    { value: '2-years', label: '2 Years', description: 'Data removed after 2 years' },
    { value: 'permanent', label: 'Permanent', description: 'Data stored permanently' }
  ];

  const visibilityOptions = [
    { value: 'private', label: 'Private', description: 'Only visible to you' },
    { value: 'public', label: 'Public', description: 'Visible to everyone' },
    { value: 'unlisted', label: 'Unlisted', description: 'Accessible via direct link only' }
  ];

  return (
    <div className="space-y-6">
      {/* IPFS Settings */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Globe" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">IPFS Gateway Settings</h3>
        </div>
        
        <Select
          label="Preferred IPFS Gateway"
          value={settings?.ipfsGateway}
          onChange={(value) => onChange('ipfsGateway', value)}
          options={ipfsGatewayOptions}
          description="Choose your preferred IPFS gateway for accessing archived content"
        />
      </div>

      {/* Data Retention */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Data Retention</h3>
        </div>
        
        <Select
          label="Data Retention Period"
          value={settings?.dataRetention}
          onChange={(value) => onChange('dataRetention', value)}
          options={retentionOptions}
          description="How long to keep your notarized content stored"
        />
      </div>

      {/* Archive Visibility */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Eye" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Archive Visibility</h3>
        </div>
        
        <Select
          label="Default Archive Visibility"
          value={settings?.archiveVisibility}
          onChange={(value) => onChange('archiveVisibility', value)}
          options={visibilityOptions}
          description="Default visibility setting for new archives"
        />
      </div>

      {/* Privacy Options */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Privacy Options</h3>
        </div>
        
        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4">
            <Checkbox
              checked={settings?.analyticsOptOut}
              onChange={(e) => onChange('analyticsOptOut', e?.target?.checked)}
              label="Opt out of Analytics"
              description="Disable usage analytics collection to enhance privacy"
            />
          </div>
        </div>
      </div>

      {/* Data Usage Summary */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="BarChart" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Data Usage Summary</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="HardDrive" size={20} className="text-blue-600" />
              <h4 className="font-medium text-foreground">Storage Used</h4>
            </div>
            <p className="text-2xl font-bold text-foreground mb-1">245 MB</p>
            <p className="text-sm text-muted-foreground">of 1 GB available</p>
            <div className="w-full bg-border rounded-full h-2 mt-2">
              <div className="bg-primary h-2 rounded-full" style={{ width: '24.5%' }}></div>
            </div>
          </div>
          
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Archive" size={20} className="text-green-600" />
              <h4 className="font-medium text-foreground">Total Archives</h4>
            </div>
            <p className="text-2xl font-bold text-foreground mb-1">127</p>
            <p className="text-sm text-muted-foreground">archived webpages</p>
            <div className="flex items-center space-x-2 mt-2">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                <span className="text-xs text-muted-foreground">89 Public</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <span className="text-xs text-muted-foreground">38 Private</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={18} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Privacy Notice</h4>
            <p className="text-sm text-blue-800">
              Your privacy is important to us. All data is encrypted and stored securely. We never share your personal information with third parties without your consent.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;